import { useVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useRef } from "react";

import { useKioskSessionLocale } from "~/stores/kiosk-session/hooks/use-kiosk-session";
import { useKioskDisplayProducts } from "~/stores/kiosk-session/hooks/use-kiosk-products-display";
import { useKioskActiveCategory } from "~/stores/kiosk-session/hooks/use-kiosk-active-category";
import { useCurrentTillLocalisation } from "~/api/tills/hooks/use-till-current/localisation";

import { KioskProductListCard } from "./kiosk-product-list-card";

// Constants for grid layout
const GRID_COLS = 3;
const CARD_HEIGHT = 300; // Height of product card + gap

export const Products = () => {
  const displayProducts = useKioskDisplayProducts();

  const { currency } = useCurrentTillLocalisation();
  const locale = useKioskSessionLocale();
  const containerRef = useRef<HTMLDivElement>(null);
  const activeCategory = useKioskActiveCategory();

  // Calculate number of rows based on products length and grid columns
  const rowCount = Math.ceil(displayProducts.length / GRID_COLS);

  const rowVirtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => containerRef.current,
    estimateSize: () => CARD_HEIGHT,
    overscan: 2
  });

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [activeCategory?.id]);

  return (
    <div
      ref={containerRef}
      className="flex-1 overflow-y-auto px-8 contain-strict"
    >
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: "100%",
          position: "relative"
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          // Calculate the products for this row
          const rowStartIndex = virtualRow.index * GRID_COLS;
          return (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={rowVirtualizer.measureElement}
              className="grid grid-cols-3 gap-8 py-8"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtualRow.start}px)`
              }}
            >
              {Array.from({ length: GRID_COLS }).map((_, colIndex) => {
                const productIndex = rowStartIndex + colIndex;
                const item = displayProducts[productIndex];

                if (!item) return null;

                const discountId = item.discounts.find(
                  (discount) => discount.type === "stamp-card"
                )?.id;

                return (
                  <KioskProductListCard
                    key={`${item.product.id}-${discountId || "no-discount"}`}
                    item={item}
                    currency={currency}
                    locale={locale}
                  />
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};
